import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, homeStats, clientLogos } from '../data/mock';
import { Lightbulb, Smile, TrendingUp } from 'lucide-react';
import SEO from '../components/SEO';

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [displayValue, setDisplayValue] = useState('0');
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateValue();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateValue = () => {
    const match = value.match(/([+\-]?)(\d+)(%?)/);
    if (!match) {
      setDisplayValue(value);
      return;
    }

    const prefix = match[1] || '';
    const targetNumber = parseInt(match[2], 10);
    const suffix = match[3] || '';
    const startTime = performance.now();

    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      const currentValue = Math.floor(easeOutExpo * targetNumber);
      setDisplayValue(`${prefix}${currentValue}${suffix}`);

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(updateCounter);
  };

  return (
    <span ref={counterRef} className="tabular-nums">
      {displayValue}
    </span>
  );
};

const HomePage = () => {
  const logoTrackRef = useRef(null);

  useEffect(() => {
    const track = logoTrackRef.current;
    if (track) {
      let position = 0;
      const speed = 0.45;
      const animate = () => {
        position -= speed;
        if (position <= -track.scrollWidth / 2) {
          position = 0;
        }
        track.style.transform = `translateX(${position}px)`;
        requestAnimationFrame(animate);
      };
      const animationId = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationId);
    }
  }, []);

  const getStatIcon = (icon) => {
    const cls = 'w-8 h-8 text-brand-terracotta';
    switch (icon) {
      case 'lightbulb':
        return <Lightbulb className={cls} strokeWidth={1.25} />;
      case 'smile':
        return <Smile className={cls} strokeWidth={1.25} />;
      case 'trending-up':
        return <TrendingUp className={cls} strokeWidth={1.25} />;
      default:
        return null;
    }
  };

  return (
    <div className="overflow-hidden">
      <SEO
        title="Événementiel entreprise Lyon | Team building & afterworks"
        description="Mon Evénemenciel, votre partenaire événementiel en entreprise à Lyon. Afterworks, team building, séminaires, soirées d'entreprise et animations sur mesure. Créons ensemble des moments inoubliables !"
        keywords="événementiel entreprise Lyon, team building, afterwork, séminaire entreprise, animation corporate, soirée entreprise, cohésion équipe, QVT"
        url="/"
        image="/img/photo-accueil-mon-evenemenciel.webp"
      />

      {/* Hero — full-bleed, brand first */}
      <section className="relative min-h-[90vh] md:min-h-screen flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat img-treatment"
          style={{ backgroundImage: `url(${siteConfig.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-petrol/55 via-brand-petrol/45 to-brand-petrol/70" />

        <div className="relative z-10 flex flex-col items-center justify-center w-full text-center px-4 py-24 animate-subtle-rise">
          <img
            src={siteConfig.logoUrlLight}
            alt={siteConfig.name}
            className="h-28 md:h-40 lg:h-48 w-auto mb-8 object-contain drop-shadow-lg"
            fetchPriority="high"
            decoding="async"
          />
          <h1 className="text-white/95 text-xl md:text-3xl lg:text-4xl font-display italic max-w-3xl mb-10">
            {siteConfig.tagline}
          </h1>
          <Link
            to="/notre-offre"
            className="inline-block bg-brand-terracotta text-white px-8 py-3.5 rounded-md text-base md:text-lg font-medium tracking-wide hover:bg-[#7a3826] transition-all duration-300"
          >
            Découvrir notre offre
          </Link>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 90"
            className="w-full h-14 md:h-20 fill-brand-ivory"
            preserveAspectRatio="none"
          >
            <path d="M0,90 C480,10 960,10 1440,90 L1440,90 L0,90 Z" />
          </svg>
        </div>
      </section>

      {/* Offer */}
      <section id="section--offre" className="py-16 md:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-brand-terracotta mb-3">
                Découvrir
              </p>
              <h2 className="font-display text-3xl md:text-5xl text-brand-petrol mb-3">
                Notre offre
              </h2>
              <h3 className="font-display italic text-xl md:text-2xl text-brand-terracotta mb-6">
                Mon Evénemenciel votre facilitateur de lien social en entreprise
              </h3>
              <p className="text-brand-petrol/70 mb-4 leading-relaxed text-sm md:text-base">
                Vous souhaitez créer du lien dans votre entreprise, faire vivre une expérience commune à vos collaborateurs, fédérer vos équipes, remercier & fidéliser vos clients, valoriser l'image de l'entreprise ?
              </p>
              <p className="text-brand-petrol/70 mb-4 leading-relaxed text-sm md:text-base">
                Mon Evénemenciel se positionne en facilitateur social à travers son offre et ses solutions d'événements, il permet de voir l'entreprise autrement !
              </p>
              <p className="text-brand-petrol/70 mb-8 leading-relaxed text-sm md:text-base">
                Alors on embarque pour vos temps forts organisés de A à Z ?
              </p>
              <Link
                to="/notre-offre"
                className="inline-block border border-brand-terracotta text-brand-terracotta px-7 py-2.5 rounded-md font-medium hover:bg-brand-terracotta hover:text-white transition-all duration-300 text-sm md:text-base"
              >
                En savoir plus
              </Link>
            </div>
            <div className="relative">
              <div className="overflow-hidden aspect-[4/5] max-w-md mx-auto shadow-2xl shadow-brand-petrol/20">
                <img
                  src="/img/photo-evenement.webp"
                  alt="Événement en entreprise"
                  className="w-full h-full object-cover img-treatment img-zoom"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 w-24 h-24 border border-brand-terracotta/40 pointer-events-none hidden md:block" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats — no heavy cards */}
      <section className="py-16 md:py-24 bg-brand-petrol text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {homeStats.map((stat, index) => (
              <div
                key={index}
                className="text-center md:text-left border-t border-white/15 pt-8"
              >
                <div className="flex justify-center md:justify-start mb-4">
                  {getStatIcon(stat.icon)}
                </div>
                <div className="font-display text-4xl md:text-5xl text-brand-terracotta-light mb-3">
                  <AnimatedCounter value={stat.value} duration={2000} />
                </div>
                <p className="text-white/65 text-sm md:text-base leading-relaxed">
                  {stat.label}
                </p>
                {stat.sublabel && (
                  <p className="text-brand-terracotta-light font-display italic mt-2 text-sm md:text-base">
                    {stat.sublabel}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QVT */}
      <section className="py-16 md:py-24 bg-brand-ivory">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-terracotta mb-3">
            Vision
          </p>
          <h2 className="font-display text-3xl md:text-5xl text-brand-petrol mb-3">
            La stratégie QVT
          </h2>
          <h3 className="font-display italic text-xl md:text-2xl text-brand-terracotta mb-8">
            Mais quésaco la QVT?
          </h3>
          <p className="text-brand-petrol/70 mb-4 leading-relaxed text-sm md:text-base">
            Une stratégie QVT, qualité de vie au travail, favorise le bien-être pour les collaborateurs de l'entreprise. Elle amène une dynamique positive sur le lieu de travail entrainant l'implication de chacun & favorisant l'épanouissement de tous !
          </p>
          <p className="text-brand-petrol/70 mb-8 leading-relaxed text-sm md:text-base">
            L'entreprise quant à elle bénéficiera de plus de productivité et de performance grâce à ses collaborateurs <strong className="text-brand-terracotta font-semibold">HEUREUX</strong> !
          </p>
          <Link
            to="/groupe"
            className="inline-block bg-brand-terracotta text-white px-7 py-2.5 rounded-md font-medium hover:bg-[#7a3826] transition-all duration-300 text-sm md:text-base"
          >
            Découvrir le groupe
          </Link>
        </div>
      </section>

      {/* Clients */}
      <section className="py-16 md:py-20 bg-white/60 border-y border-brand-petrol/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-brand-petrol mb-2">
              Nos réalisations
            </h2>
            <h3 className="font-display italic text-lg md:text-xl text-brand-terracotta">
              Ils ont fait confiance à notre petit grain de folie ...
            </h3>
          </div>

          <div className="overflow-hidden">
            <div
              ref={logoTrackRef}
              className="flex items-center gap-14 whitespace-nowrap"
              style={{ width: 'fit-content' }}
            >
              {[...clientLogos, ...clientLogos].map((logo, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 h-14 w-28 flex items-center justify-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={logo.url}
                    alt={logo.name}
                    className="max-h-full max-w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
